import {
  ALLOWED_AUSTRALIA_POST_RETURN_SERVICES,
  AsendiaServices,
  RoyalMailV3Services,
  UpsMailInnovationsServices,
} from "./carrier-service-catalog";
import {
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  UspsServices,
} from "./carrier-service-enums";

export enum ServiceLevelDirection {
  OUTBOUND = "outbound",
  RETURN = "return",
}

// Return-specific products; the hint fallback for carriers without curated hints.
const RETURN_PRODUCTS: ReadonlyMap<string, ReadonlySet<string>> = new Map<
  string,
  ReadonlySet<string>
>([
  [
    Carriers.USPS,
    new Set<string>([
      UspsServices.GroundReturn,
      UspsServices.GroundAdvantageReturn,
      UspsServices.PriorityMailReturn,
      UspsServices.FirstClassPkgReturn,
      UspsServices.PriorityMailExpressReturn,
    ]),
  ],
  [
    Carriers.DHL_ECS,
    new Set<string>([
      DhlEcsServices.DhlSmartMailParcelReturnGround,
      DhlEcsServices.DhlSmartMailParcelReturnPlus,
      DhlEcsServices.DhlSmartMailParcelReturnLight,
    ]),
  ],
  [
    Carriers.ASENDIA,
    new Set<string>([
      AsendiaServices.ePAQReturnsDomestic,
      AsendiaServices.ePAQReturnsInternational,
    ]),
  ],
  [
    Carriers.UPS_MAIL_INNOVATIONS,
    new Set<string>([UpsMailInnovationsServices.SingleReturns]),
  ],
]);

// Services to point merchants at for return labels, with why. Carriers
// without an entry fall back to their return products.
const RETURN_SERVICE_HINTS: ReadonlyMap<
  string,
  { services: ReadonlySet<string>; hint: string }
> = new Map([
  [
    Carriers.AUSTRALIA_POST,
    {
      services: ALLOWED_AUSTRALIA_POST_RETURN_SERVICES,
      hint: "Australia Post's eParcel returns products. Other services aren't manifested for returns and incur penalty fees.",
    },
  ],
  [
    Carriers.ROYAL_MAIL_V3,
    {
      // Only TrackedReturns48 is bought for returns (see replaceRoyalMailRates).
      services: new Set<string>([RoyalMailV3Services.TrackedReturns48]),
      hint: "Royal Mail's Tracked Returns product, built for return labels.",
    },
  ],
  [
    Carriers.CANADA_POST,
    {
      services: new Set<string>([
        CanadaPostServices.RegularParcel,
        CanadaPostServices.ExpeditedParcel,
        CanadaPostServices.ExpeditedParcelPlus,
        CanadaPostServices.Xpresspost,
        CanadaPostServices.Priority,
      ]),
      hint: "Available through Canada Post authorized returns (domestic only).",
    },
  ],
]);

const RETURN_PRODUCT_HINT = "A dedicated return product for this carrier.";

/** Why a service suits return labels, or undefined when it isn't a return service. */
export function getReturnServiceHint(
  carrier: string,
  service: string,
): string | undefined {
  const curated = RETURN_SERVICE_HINTS.get(carrier);
  if (curated) {
    return curated.services.has(service) ? curated.hint : undefined;
  }
  return RETURN_PRODUCTS.get(carrier)?.has(service)
    ? RETURN_PRODUCT_HINT
    : undefined;
}
